import type { Paths } from '@zg-rentals/paths-manifest';

import contextUtils from '../utils/contextUtils';
import StringConstants from '../constants/StringConstants';
import AddPropertyConstants from '../constants/AddPropertyConstants';
import PropertyConstants from '../constants/PropertyConstants';
import Constants from '../constants';

import ApplicationsSummary from '../model/ApplicationsSummary';
import ContactEmailDeliverability from '../model/ContactEmailDeliverability';
import VrModel from '../model/VrModel';
import Unit from '../model/Unit';
import ExternalTour from '../model/ExternalTour';
import SingleFamilyContract from '../model/SingleFamilyContract';
import ContactHours from '../model/ContactHours';

import type { UserPropertyMultiEditRequestWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

type multiSaveProps = {
  listingAlias: string;
  saveToParentKey: string;
  fieldName: string;
  value: unknown;
  floorPlanId: string;
};

const ROOMMATE_GENDER_PREFERENCE = {
  MALE: 'male',
  FEMALE: 'female',
  NON_BINARY: 'nonBinary',
  NO_PREFERENCE: 'noPreference',
};

const SAVE_TO_PARENT_KEY = {
  FLOOR_PLANS: 'FLOOR_PLANS',
  FLOOR_PLANS_ROOM_FOR_RENT: 'FLOOR_PLANS_ROOM_FOR_RENT',
  PROPERTY: 'PROPERTY',
  ROOM_FOR_RENT: 'ROOM_FOR_RENT',
};

const UPDATE_CURRENT_PROPERTY_KEY = {
  CURRENT_PETS: 'CURRENT_PETS',
  ROOM_FOR_RENT: 'ROOM_FOR_RENT',
  ROOM_FOR_RENT_CURRENT_ROOMMATES_DETAILS: 'ROOM_FOR_RENT_CURRENT_ROOMMATES_DETAILS',
  ROOMMATE_REQUIREMENTS: 'ROOMMATE_REQUIREMENTS',
  FLOOR_PLANS_ROOM_FOR_RENT: 'FLOOR_PLANS_ROOM_FOR_RENT',
  FLOOR_PLANS: 'FLOOR_PLANS',
};

const postingUtils = {
  formatPriceFromApi(lowPrice: string | undefined, highPrice: string | undefined) {
    if (typeof lowPrice === 'undefined') {
      return '';
    }
    if (lowPrice !== highPrice) {
      return `${lowPrice}-${highPrice}`;
    }
    return lowPrice;
  },
  formatBedsFromApi(beds: number | undefined) {
    return (beds || beds === 0) && beds >= 0 ? beds : '';
  },
  getDestinationTab(paths: Paths, search: string) {
    const isFromMarketingPaymentsRoute = contextUtils.isFromMarketingPaymentsRoute(search);
    const isFromMarketingApplicationsRoute = contextUtils.isFromMarketingApplicationsRoute(search);
    const isFromMarketingLeasesRoute = contextUtils.isFromMarketingLeasesRoute(search);
    let destinationTab = paths.propertyDetailsListing;
    if (isFromMarketingPaymentsRoute) {
      // @ts-ignore
      destinationTab = Constants.MARKETING_PATH.PAYMENTS;
    } else if (isFromMarketingApplicationsRoute) {
      // @ts-ignore
      destinationTab = Constants.MARKETING_PATH.APPLICATIONS;
    } else if (isFromMarketingLeasesRoute) {
      // @ts-ignore
      destinationTab = Constants.MARKETING_PATH.LEASES;
    }
    return destinationTab;
  },
  getDestinationUrl(paths: Paths, buttonMode: string, listingAlias: string) {
    switch (buttonMode) {
      case AddPropertyConstants.ADD_MODE.APPLICATIONS:
        return `${paths.base}/${StringConstants.MARKETING_PATH.APPLICATIONS}?propertyId=${listingAlias}`;
      case AddPropertyConstants.ADD_MODE.PAYMENTS:
        return `${paths.base}/${StringConstants.MARKETING_PATH.PAYMENTS}/properties/${listingAlias}`;
      case AddPropertyConstants.ADD_MODE.LEASES:
        return `${paths.base}/leasing/property/${listingAlias}`;
      case AddPropertyConstants.ADD_MODE.LIST:
        return `${paths.propertiesPage}/${listingAlias}/listing`;
      case AddPropertyConstants.ADD_MODE.INTENT_SELECTION:
        return `${paths.propertiesPage}/${listingAlias}/select-actions`;
      default:
        return `${paths.propertiesPage}/${listingAlias}/${paths.propertyDetailsListing}`;
    }
  },
  getRoommateGenderPreferences(genderObj: Record<string, boolean> = {}) {
    const preferencesList = [];

    if (genderObj.hasRoommateGenderPreferenceMale) {
      preferencesList.push(ROOMMATE_GENDER_PREFERENCE.MALE);
    }

    if (genderObj.hasRoommateGenderPreferenceFemale) {
      preferencesList.push(ROOMMATE_GENDER_PREFERENCE.FEMALE);
    }

    if (genderObj.hasNoRoommateGenderPreference) {
      preferencesList.push(ROOMMATE_GENDER_PREFERENCE.NO_PREFERENCE);
    }

    if (genderObj.hasRoommateGenderPreferenceNonBinary) {
      preferencesList.push(ROOMMATE_GENDER_PREFERENCE.NON_BINARY);
    }

    return preferencesList;
  },
  transformPropertyListingTypeForApi(formData: {
    listingType: keyof typeof PropertyConstants.LISTING_CODES_BY_TYPE;
    propertyType: (typeof PropertyConstants.PROPERTY_TYPES)[keyof typeof PropertyConstants.PROPERTY_TYPES];
    fullAddress: string;
    unitNumber: string;
    numUnits: string;
    customerId: string;
  }) {
    const { LISTING_CODES_BY_TYPE, PROPERTY_CODES_BY_TYPE } = PropertyConstants;
    return {
      listingTypeCode: LISTING_CODES_BY_TYPE[formData.listingType],
      propertyTypeCode: PROPERTY_CODES_BY_TYPE[formData.propertyType],
      fullAddress: formData.fullAddress,
      unitNumber: formData.unitNumber,
      numUnits: formData.numUnits,
      customerId: formData.customerId,
    };
  },
  mapFormDataToApi(field: string) {
    // Maps form data to specific API endpoints that ultimately update the API.
    // Note, these API endpoints may be different from the actual data that the API returns.
    const mappedData = {
      additionalEmail: 'additionalEmail',
      isListingExclusive: 'isBrokerExclusive',
      agentLicenseType: 'agentLicenseType',
      hasCentralAirConditioning: 'hasCentralAirConditioning',
      hasWallAirConditioning: 'hasWallAirConditioning',
      hasWindowAirConditioning: 'hasWindowAirConditioning',
      hasForcedAirHeatingSystem: 'hasForcedAirHeatingSystem',
      hasBaseboardHeatingSystem: 'hasBaseboardHeatingSystem',
      hasHeatPumpHeatingSystem: 'hasHeatPumpHeatingSystem',
      hasWallHeatingSystem: 'hasWallHeatingSystem',
      propertyAmenities: 'propertyAmenities',
      apartmentAmenities: 'apartmentAmenities',
      isApplicationsTogglePristine: 'isApplicationsTogglePristine',
      isApplicationsAccepted: 'isApplicationsAccepted',
      balconyOrDeck: 'balconyDeckPatioPorch',
      contactEmail: 'email',
      contactPhone: 'phone',
      contactName: 'contactName',
      contactSunday: 'contactSunday',
      contactMonday: 'contactMonday',
      contactTuesday: 'contactTuesday',
      contactWednesday: 'contactWednesday',
      contactThursday: 'contactThursday',
      contactFriday: 'contactFriday',
      contactSaturday: 'contactSaturday',
      city: 'city',
      description: 'fullDescription',
      floorplans: {
        amenities: 'unitAmenities',
        baths: 'numBathrooms',
        beds: 'numBedrooms',
        dateAvailable: 'dateAvailable',
        floorPlanId: 'id',
        floorPlanPhotos: 'floorPlanPhotos',
        layoutPhotos: 'layout',
        name: 'name',
        active: 'active',
        price: 'price',
        lowPrice: 'lowPrice',
        highPrice: 'highPrice',
        securityDeposit: 'securityDeposit',
        squareFeet: 'squareFeet',
        units: 'units',
      },
      hasAttachedGarageParking: 'hasAttachedGarageParking',
      hasDetachedGarageParking: 'hasDetachedGarageParking',
      hasBicycleStorage: 'hasBicycleStorage',
      hasDishWasherAppliance: 'hasDishwasherAppliance',
      hasSwimmingPool: 'hasSwimmingPool',
      hidePhone: 'hidePhone',
      housingConnectorStatus: 'housingConnectorStatus',
      specialOffers: 'specialOffers',
      forRentBy: 'forRentBy',
      isFurnished: 'isFurnished',
      hasHardwoodFloorCovering: 'hasHardwoodFloorCovering',
      hideAddress: 'hideStreetAddress',
      laundry: 'laundry',
      leaseDuration: 'leaseDuration',
      leaseTerms: 'leaseTerms',
      listingTitle: 'name',
      listingType: 'listingType',
      // listingTitle
      name: 'name',
      numUnits: 'unitsInBuilding',
      offStreetParking: 'offStreetParking',
      photos: 'propertyPhotos',
      propertyType: 'propertyType',
      specials: 'previewMessage',
      state: 'state',
      street: 'street',
      unit: 'unit',
      website: 'webSite',
      wheelchairAccess: 'wheelchairAccess',
      zip: 'zip',
    };

    let apiProperty;
    const propertyName = field.split('.');
    // eslint-disable-next-line constellation/word-list
    if (propertyName[0] === 'floorplans') {
      // @ts-ignore
      apiProperty = mappedData.floorplans[propertyName[1]];
    } else {
      // @ts-ignore
      apiProperty = mappedData[propertyName[0]];
    }

    return apiProperty;
  },
  // TODO: update this once the API is updated with autogenerated TS types
  // eslint-disable-next-line
  transformDatafromApi(apiData: Record<string, any>) {
    const { LISTING_TYPES_BY_CODE, PROPERTY_TYPES_BY_CODE } = PropertyConstants;
    const dataFromApi = apiData;
    // @ts-ignore
    const listingType = LISTING_TYPES_BY_CODE[dataFromApi.listingType.code];
    const propertyType = PROPERTY_TYPES_BY_CODE[dataFromApi.propertyType.code];

    const apiFloorplans = this.transformFloorplanData(dataFromApi.floorPlans);
    const apiUnits = (dataFromApi.units || []).map(Unit);

    let buildingAttributes = null;
    let airConditioning = null;
    let furnished = null;
    let hardwoodFloors = null;
    if (dataFromApi.hasOwnProperty('buildingAttributes')) {
      buildingAttributes = dataFromApi.buildingAttributes;

      if (buildingAttributes.hasOwnProperty('airConditioningTypes')) {
        airConditioning = buildingAttributes.airConditioningTypes.hasAny;
      }

      if (buildingAttributes.hasOwnProperty('isFurnished')) {
        furnished = buildingAttributes.isFurnished;
      }

      if (buildingAttributes.hasOwnProperty('floorCoveringTypes')) {
        hardwoodFloors = buildingAttributes.floorCoveringTypes.hasHardwood;
      }
    }

    const vrModels = dataFromApi.vrModels && dataFromApi.vrModels.map((tour: Record<string, unknown>) => VrModel(tour));
    const externalTours =
      dataFromApi.externalTours && dataFromApi.externalTours.map((tour: Record<string, unknown>) => ExternalTour(tour));

    const newFormData = {
      active: dataFromApi.active,
      additionalEmail: dataFromApi.additionalEmail,
      adproduct: dataFromApi.adproduct,
      aiDescription: dataFromApi.aiDescriptionDto,
      applicationsSummary: ApplicationsSummary(dataFromApi.applicationsSummary),
      hasWallAirConditioning: dataFromApi.hasWallAirConditioning,
      hasWindowAirConditioning: dataFromApi.hasWindowAirConditioning,
      hasCentralAirConditioning: dataFromApi.hasCentralAirConditioning,
      hasForcedAirHeatingSystem: dataFromApi.hasForcedAirHeatingSystem,
      hasBaseboardHeatingSystem: dataFromApi.hasBaseboardHeatingSystem,
      hasHeatPumpHeatingSystem: dataFromApi.hasHeatPumpHeatingSystem,
      hasWallHeatingSystem: dataFromApi.hasWallHeatingSystem,
      amenities: dataFromApi.amenities,
      propertyAmenities: dataFromApi.propertyAmenities,
      amenitiesText: '',
      apartmentAmenities: dataFromApi.apartmentAmenities,
      canBeArchived: dataFromApi.canBeArchived,
      canBeRelisted: dataFromApi.canBeRelisted,
      balconyOrDeck: dataFromApi.balconyDeckPatioPorch,
      buildingAttributes: dataFromApi.buildingAttributes,
      contactEmail: dataFromApi.email,
      contactEmailDeliverability: ContactEmailDeliverability(dataFromApi.contactEmailDeliverability),
      contactHours: ContactHours(dataFromApi.contactHours),
      contactName: dataFromApi.contactName,
      contactPhone: dataFromApi.phone,
      contactPhoneDateVerified: dataFromApi.datePhoneVerified,
      city: dataFromApi.city,
      customerId: dataFromApi.customerId,
      dateLastActivated: dataFromApi.dateLastActivated,
      dateLastActivatedTimestamp: dataFromApi.dateLastActivatedTimestamp,
      dateUpdatedTimestamp: dataFromApi.dateUpdatedTimestamp,
      dateUpdated: dataFromApi.dateUpdated,
      deliveryMethod: dataFromApi.deliveryMethod,
      description: dataFromApi.fullDescription,
      dateEmailVerified: dataFromApi.dateEmailVerified,
      displayTotalMonthlyPriceWithFee: dataFromApi.displayTotalMonthlyPriceWithFee,
      expirationDate: dataFromApi.expirationDate,
      expirationDateTimestamp: dataFromApi.expirationDateTimestamp,
      externalTours,
      feedId: dataFromApi.feedId,
      feedListingId: dataFromApi.feedListingId,
      listingAlias: dataFromApi.listingAlias,
      feedName: dataFromApi.feedName,
      floorplans: apiFloorplans,
      isListingExclusive: dataFromApi.agent && dataFromApi.agent.isExclusive,
      agentLicenseType: dataFromApi.agent && dataFromApi.agent.licenseType,
      units: apiUnits,
      forRentBy: dataFromApi.forRentBy,
      forSaleExists: dataFromApi.forSaleExits,
      forSaleUrlOnZillow: dataFromApi.forSaleUrlOnZillow,
      isFurnished: furnished !== null ? furnished : dataFromApi.isFurnished,
      geo: dataFromApi.geo,
      hasHardwoodFloorCovering: hardwoodFloors !== null ? hardwoodFloors : dataFromApi.hasHardwoodFloorCovering,
      hasActiveMultifamilyContract: dataFromApi.hasActiveMultifamilyContract,
      hasAnalyticsRentComps: dataFromApi.hasAnalyticsRentComps,
      hasAttachedGarageParking: dataFromApi.hasAttachedGarageParking,
      hasDetachedGarageParking: dataFromApi.hasDetachedGarageParking,
      hasBicycleStorage: dataFromApi.hasBicycleStorage,
      hasDishWasherAppliance: dataFromApi.hasDishWasherAppliance,
      hasSwimmingPool: dataFromApi.hasSwimmingPool,
      hideAddress: dataFromApi.hideStreetAddress,
      hidePhone: dataFromApi.hidePhone,
      //  ZRM-10946: API omits isAddressValid on feed listings (for which address is not editable).
      //  Default to true if value doesn't exist on response
      isAddressValid: typeof dataFromApi.isAddressValid !== 'undefined' ? dataFromApi.isAddressValid : true,
      isArchived: dataFromApi.isArchived,
      isContactEmailVerified: dataFromApi.isContactEmailVerified,
      singleFamilyContract: SingleFamilyContract(dataFromApi.singleFamilyContractDto),
      isAddressEditable: dataFromApi.isAddressEditable,
      isEditable: dataFromApi.isEditable,
      isConvertedFromFeed: dataFromApi.isConvertedFromFeed,
      isFeed: dataFromApi.isFeed,
      isViewable: dataFromApi.isViewable,
      canSubmitMapPinRequest: dataFromApi.canSubmitFixMapPinRequest,
      isMultifamily: dataFromApi.isMultifamily,
      isPaidMultifamily: dataFromApi.isPaidMultifamily,
      isApplicationsTogglePristine: dataFromApi.isApplicationsTogglePristine,
      isApplicationsAccepted: dataFromApi.isApplicationsAccepted,
      isApplicationsInviteOnly: dataFromApi.isApplicationsInviteOnly,
      isApplicationsInviteOnlyEnabled: dataFromApi.isApplicationsInviteOnlyEnabled,
      isPackageProduct: dataFromApi.isPackageProduct,
      isWaitlisted: dataFromApi.isWaitlisted,
      laundry: dataFromApi.laundry,
      leaseDuration: dataFromApi.leaseDuration,
      leaseTerms: dataFromApi.leaseTerms,
      listingAvailability: dataFromApi.listingAvailability,
      listingId: dataFromApi.listingId,
      listingTitle: dataFromApi.name,
      listingType,
      isContactInfoEditable: dataFromApi.isContactInfoEditable,
      listingTypeCode: dataFromApi.listingType.code,
      logo: dataFromApi.logo,
      modularListingProgress: dataFromApi.modularListingProgress,
      name: dataFromApi.name,
      numUnits: dataFromApi.numUnits,
      hasZillowRentConnectBasePackage: dataFromApi.hasZillowRentConnectBasePackage,
      isActivationBlocked: dataFromApi.isActivationBlocked,
      housingConnectorStatus: dataFromApi.housingConnectorStatus,
      offStreetParking: dataFromApi.offStreetParking,
      localeSpecificNotice: dataFromApi.localeSpecificNotice,
      partialListing: dataFromApi.partialListing,
      performance: dataFromApi.performance,
      petsAllowed: dataFromApi.petsAllowed,
      photos: dataFromApi.propertyPhotos,
      preferredContactEmail: dataFromApi.preferredContactEmail,
      productType: dataFromApi.productType,
      propertyType,
      propertyTypeCode: dataFromApi.propertyType.code,
      rentersInsurance: dataFromApi.rentersInsurance,
      rentGuarantee: dataFromApi.rentGuarantee,
      rentZestimate: dataFromApi.rentZestimate,
      restrictions: dataFromApi.restrictions,
      roomForRent: dataFromApi.roomForRent,
      selfServeOnboardingStatus: dataFromApi.selfServeOnboardingStatus,
      specials: dataFromApi.previewMessage,
      specialOffers: dataFromApi.specialOffers,
      state: dataFromApi.state,
      street: dataFromApi.street,
      syndication: dataFromApi.syndication,
      topDupesDetails: dataFromApi.topDupesDetails,
      totalMonthlyPriceWithFee: dataFromApi.totalMonthlyPriceWithFee,
      totalMonthlyPriceWithFeeNotice: dataFromApi.totalMonthlyPriceWithFeeNotice,
      trackingPhoneNumber: dataFromApi.trackingPhoneNumber,
      unit: dataFromApi.unit,
      verified: !dataFromApi.needsPhoneVerification,
      views: dataFromApi.views,
      website: dataFromApi.website,
      wheelchairAccess: dataFromApi.wheelchairAccess,
      zip: dataFromApi.zip,
      vrModels,
      multiToSingleFamilyConversion: dataFromApi.multiToSingleFamilyConversion,
      dateCreated: dataFromApi.dateCreated,
      updateActiveStatus: dataFromApi.updateActiveStatus,
      listingCriteria: dataFromApi.listingCriteria,
      videoUrl: dataFromApi.videoUrl,
      webAnalytics: dataFromApi.webAnalytics,
    };
    return newFormData;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformFloorplanData(floorplans: Array<Record<string, any>> = []) {
    const apiFloorplans = (floorplans || []).map((floorplan) => ({
      floorPlanId: floorplan.id,
      active: typeof floorplan.active === 'undefined' ? true : floorplan.active,
      layoutPhoto: floorplan.layout || {},
      floorPlanPhotos: floorplan.floorPlanPhotos || [],
      price: this.formatPriceFromApi(floorplan.lowPrice, floorplan.highPrice) || '',
      lowPrice: floorplan.lowPrice || '',
      highPrice: floorplan.highPrice || '',
      roomForRent: floorplan.roomForRent,
      securityDeposit: floorplan.securityDeposit,
      baths: floorplan.numBathrooms || '',
      beds: this.formatBedsFromApi(floorplan.numBedrooms),
      squareFeet: floorplan.squareFeet || '',
      dateAvailable: floorplan.dateAvailable || '',
      name: floorplan.name || '',
      unitAmenities: floorplan.unitAmenities || [],
      units: (floorplan.units || []).map(Unit),
      vrModel: VrModel(floorplan.vrModel),
      externalTour: ExternalTour(floorplan.externalTour),
    }));

    return apiFloorplans;
  },
};

const multiFieldsUtil = {
  getDataToSave: ({
    listingAlias,
    saveToParentKey,
    fieldName,
    value,
    floorPlanId,
  }: multiSaveProps): UserPropertyMultiEditRequestWebDto => {
    let dataToSave = {} as UserPropertyMultiEditRequestWebDto;

    switch (saveToParentKey) {
      case SAVE_TO_PARENT_KEY.ROOM_FOR_RENT:
        dataToSave = {
          property: {
            listingAlias,
            roomForRent: {
              [fieldName]: value,
            },
          },
        };
        break;
      case SAVE_TO_PARENT_KEY.FLOOR_PLANS_ROOM_FOR_RENT:
        dataToSave = {
          property: {
            listingAlias,
          },
          floorPlans: [
            {
              id: floorPlanId,
              roomForRent: {
                [fieldName]: value,
              },
            },
          ],
        };
        break;

      case SAVE_TO_PARENT_KEY.FLOOR_PLANS:
        dataToSave = {
          property: {
            listingAlias,
          },
          floorPlans: [
            {
              id: floorPlanId,
              [fieldName]: value,
            },
          ],
        };
        break;

      case SAVE_TO_PARENT_KEY.PROPERTY:
        dataToSave = {
          property: {
            listingAlias,
            [fieldName]: value,
          },
        };
        break;

      default:
        break;
    }

    return dataToSave;
  },
  getMultiEditErrorMessageFromResponse(response: unknown): string {
    const res = response as {
      property?: { success?: boolean; errors?: Array<{ message?: string }> };
      floorPlans?: Array<{ success?: boolean; errors?: Array<{ message?: string }> }>;
      success?: boolean;
    };
    const errorMessages: Array<string> = [];
    if (res?.property?.errors) {
      res.property.errors.forEach((e) => e.message && errorMessages.push(e.message));
    }
    res?.floorPlans?.forEach((fp) => {
      fp?.errors?.forEach((e) => e.message && errorMessages.push(e.message));
    });
    return errorMessages.filter(Boolean).join(', ') || '';
  },
};

// @ts-expect-error
export { multiFieldsUtil, multiSaveProps, ROOMMATE_GENDER_PREFERENCE, SAVE_TO_PARENT_KEY, UPDATE_CURRENT_PROPERTY_KEY };

export default postingUtils;
